import {
	InteractionComposedIcon,
	InteractionIconContainer,
	IconSize,
} from "@/components/molecules/interactionComposedIcon";
import {
	InteractionDto,
	InteractionStatus,
	InteractionDirection,
} from "@/repository/models";
import {
	PersonCallRegular,
	VoicemailRegular,
	CallParkRegular,
	PersonChatRegular,
	PersonMailRegular,
} from "@fluentui/react-icons";
import { Callback18Regular } from "../icons";
import { textInteractionTypes } from "./interactionHelper";
import { InteractionType } from "../types";
import { InteractionHistoryListItemDto } from "@/components/organisms/history";

export const getMonitoringIcon = (interaction: InteractionDto) => {
	switch (interaction.type) {
		case InteractionType.Call:
			return <PersonCallRegular />;
		case InteractionType.Email:
			return <PersonMailRegular />;
		case InteractionType.WhatsApp:
		case InteractionType.Webchat:
			return <PersonChatRegular />;
		default:
			return <PersonCallRegular />;
	}
};

export const getParkIcon = (interaction: InteractionDto) => {
	switch (interaction.type) {
		case InteractionType.Call:
			return <CallParkRegular />;
		case InteractionType.Email:
		case InteractionType.WhatsApp:
		case InteractionType.Webchat:
			return (
				<InteractionComposedIcon
					size={IconSize.Medium}
					interactionType={interaction.type}
					isMissed={false}
					isInbound={false}
					isParked={true}
				/>
			);
		default:
			return <PersonCallRegular />;
	}
};

export interface InteractionIconOptions {
	isMonitoredInteraction?: boolean;
	isHighlighted?: boolean;
	hasUnreadIcon?: boolean;
	iconSize?: IconSize;
	hideDirectionArrow?: boolean;
}

export const getInteractionIcon = (
	interaction: InteractionDto,
	options: InteractionIconOptions = {}
) => {
	const {
		isMonitoredInteraction = false,
		isHighlighted = false,
		hasUnreadIcon = false,
		iconSize = IconSize.Medium,
		hideDirectionArrow = false,
	} = options;

	if (isMonitoredInteraction) {
		return (
			<InteractionIconContainer
				icon={getMonitoringIcon(interaction)}
				iconSize={iconSize}
			/>
		);
	}

	if (interaction.isIVRWarmTransfer) {
		return (
			<InteractionIconContainer
				icon={<PersonCallRegular />}
				iconSize={iconSize}
			/>
		);
	}

	if (interaction.type == InteractionType.Voicemail) {
		return (
			<InteractionIconContainer
				icon={<VoicemailRegular />}
				iconSize={iconSize}
			/>
		);
	}

	const composedIcon = (
		<InteractionComposedIcon
			size={iconSize}
			interactionType={interaction.type}
			isInbound={interaction.direction === InteractionDirection.Incoming}
			isHighlighted={isHighlighted}
			hasUnreadIcon={hasUnreadIcon}
			hideDirectionArrow={hideDirectionArrow}
		/>
	);

	switch (interaction.status) {
		// The plain-channel icon format (hideDirectionArrow) drops the parked
		// badge only for text interactions, whose parked state is conveyed by
		// the status text; parked calls keep the park icon in every format.
		case InteractionStatus.Parked:
			if (hideDirectionArrow && textInteractionTypes.includes(interaction.type)) {
				return composedIcon;
			}

			return (
				<InteractionIconContainer
					icon={getParkIcon(interaction)}
					iconSize={iconSize}
				/>
			);
		case InteractionStatus.CallbackOffering:
		case InteractionStatus.CallbackRequest:
			return (
				<InteractionIconContainer
					icon={<Callback18Regular />}
					iconSize={iconSize}
				/>
			);
		default:
			return composedIcon;
	}
};

export const getHistoryInteractionIcon = (
	interactionHistoryItem: InteractionHistoryListItemDto,
	iconSize: IconSize = IconSize.Medium
) => {
	if (interactionHistoryItem.type == InteractionType.Voicemail) {
		return (
			<InteractionIconContainer
				icon={<VoicemailRegular />}
				iconSize={iconSize}
			/>
		);
	}

	return (
		<InteractionComposedIcon
			size={iconSize}
			interactionType={interactionHistoryItem.type}
			isMissed={interactionHistoryItem.isMissed}
			isInbound={interactionHistoryItem.isInboundInteraction}
		/>
	);
};
